import {
  BuildingIcon,
  FilterIcon,
  FlaskConicalIcon,
  XIcon,
} from "lucide-react";
import React, { type Key, useCallback, useMemo, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { ItemDetail } from "@/components/ui/list-box";
import { Popover } from "@/components/ui/popover";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group";
import { useMediaQuery } from "@/components/ui/utils";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { TEST_TYPE_OPTIONS } from "../constants";
import { useTestRunFilters } from "../hooks/useTestRunFilters";
import type { TestRunNew } from "../types";

function ClearFiltersButton() {
  const { hasFilters, clearFilters } = useTestRunFilters();

  return hasFilters ? (
    <Button size="sm" variant="ghost" onPress={clearFilters}>
      Clear
    </Button>
  ) : null;
}

function TestRunFilterBar({ runs }: { runs: TestRunNew[] }) {
  const { filters, hasFilters, removeFilter } = useTestRunFilters();
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();

  const availableTestSpecs = useMemo(
    () =>
      Array.from(
        new Map(
          runs.flatMap((run) => run.testSpecs).map((spec) => [spec.id, spec]),
        ).values(),
      ),
    [runs],
  );

  const onRemove = (keys: Set<Key>) => {
    for (const key of keys) {
      const [filterKey, filterValue] = key.toString().split("-");
      removeFilter(filterKey, filterValue);
    }
  };

  if (!hasFilters) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <TagGroup
        onRemove={onRemove}
        aria-label="Test run filters"
        color="primary"
        variant="outline"
      >
        <TagList className="flex items-center flex-wrap gap-2">
          {filters.labs.map((labId) => (
            <Tag
              key={`labs-${labId}`}
              id={`labs-${labId}`}
              textValue={labId.toString()}
            >
              Lab: {primaryLabs?.find((l) => l.id === labId)?.name || "-"}
            </Tag>
          ))}
          {filters.testType.map((type) => (
            <Tag
              key={`testType-${type}`}
              id={`testType-${type}`}
              textValue={type}
            >
              Type:{" "}
              {TEST_TYPE_OPTIONS.find((t) => t.value === type)?.label ?? type}
            </Tag>
          ))}
          {filters.testSpec.map((specId) => {
            const spec = availableTestSpecs?.find((s) => s.id === specId);
            return (
              <Tag
                key={`testSpec-${specId}`}
                id={`testSpec-${specId}`}
                textValue={
                  spec ? `${spec.name} (${spec.code})` : specId.toString()
                }
              >
                Spec: {spec ? `${spec.name} (${spec.code})` : "-"}
              </Tag>
            );
          })}
        </TagList>
      </TagGroup>
      <ClearFiltersButton />
    </div>
  );
}

interface FilterItemProps {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  value: string;
}

function FilterItem({ checked, onToggle, children, value }: FilterItemProps) {
  return (
    <CommandItem
      onSelect={onToggle}
      className="flex items-center gap-2"
      value={value}
    >
      <Checkbox
        isSelected={checked}
        className="border-muted-foreground flex-1 min-w-0"
        onChange={onToggle}
      >
        {children}
      </Checkbox>
    </CommandItem>
  );
}

function TestRunFilterPopover({ runs }: { runs: TestRunNew[] }) {
  const {
    filters,
    hasFilters,
    addTestType,
    removeTestType,
    addTestSpec,
    removeTestSpec,
    addLab,
    removeLab,
    clearFilters,
  } = useTestRunFilters();
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState("");
  const [search, setSearch] = useState("");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const { availableTypes, availableTestSpecs } = useMemo(() => {
    const types = Array.from(new Set(runs.flatMap((run) => run.types)));
    const specs = Array.from(
      new Map(
        runs.flatMap((run) => run.testSpecs).map((spec) => [spec.id, spec]),
      ).values(),
    );
    return { availableTypes: types, availableTestSpecs: specs };
  }, [runs]);

  const filteredTypeOptions = useMemo(
    () => TEST_TYPE_OPTIONS.filter((opt) => availableTypes.includes(opt.value)),
    [availableTypes],
  );

  useTinyKeys({
    f: (e) => {
      e.preventDefault();
      setOpen((open) => !open);
    },
  });

  const handleOpenChange = (flag: boolean) => {
    if (!flag) {
      setTimeout(() => setPage(""), 200);
    }
    setSearch("");
    setOpen(flag);
  };

  const handlePageChange = (newPage: string) => {
    setPage(newPage);
    setSearch("");
  };

  const handleTestTypeToggle = useCallback(
    (optionValue: string) => {
      const typedValue = optionValue as
        | "SEROLOGY"
        | "CLINICAL"
        | "HLA"
        | "MICROBIOLOGY";
      const isSelected = filters.testType.includes(typedValue);
      if (isSelected) {
        removeTestType(typedValue);
      } else {
        addTestType(typedValue);
      }
    },
    [filters.testType, removeTestType, addTestType],
  );

  const handleTestSpecToggle = useCallback(
    (specId: number) => {
      const isSelected = filters.testSpec.includes(specId);
      if (isSelected) {
        removeTestSpec(specId);
      } else {
        addTestSpec(specId);
      }
    },
    [filters.testSpec, removeTestSpec, addTestSpec],
  );

  const handleLabToggle = useCallback(
    (labId: number) => {
      const isSelected = filters.labs.includes(labId);
      if (isSelected) {
        removeLab(labId);
      } else {
        addLab(labId);
      }
    },
    [filters.labs, removeLab, addLab],
  );

  return (
    <DialogTrigger isOpen={open} onOpenChange={handleOpenChange}>
      <Button size="sm" variant="outline" onPress={() => setOpen(true)}>
        <FilterIcon data-slot="icon" />
        Filter
      </Button>
      <Popover
        className="p-0 sm:min-w-[220px] sm:max-w-[300px]"
        placement="bottom end"
      >
        <Dialog
          className="p-0! outline-hidden"
          aria-label="Filter"
          hideCloseButton
        >
          <Command
            className="[&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]>svg]:size-4 [&_[cmdk-item]>svg]:mr-2"
            onKeyDown={(e) => {
              if (e.key === "Escape" && page) {
                e.preventDefault();
                e.stopPropagation();
                setPage("");
                setSearch("");
              }
              if (e.key.startsWith("Arrow")) {
                e.stopPropagation();
              }
            }}
          >
            <CommandInput
              placeholder="Filter..."
              value={search}
              onValueChange={setSearch}
              autoFocus={!isMobile}
              // biome-ignore lint/complexity/noUselessFragments: required
              icon={<></>}
            />
            <CommandEmpty>No filter found</CommandEmpty>
            <CommandList>
              <CommandGroup>
                {!page && (
                  <>
                    <CommandItem onSelect={() => handlePageChange("lab")}>
                      <BuildingIcon />
                      Lab
                    </CommandItem>
                    <CommandItem onSelect={() => handlePageChange("testType")}>
                      <FlaskConicalIcon />
                      Test type
                    </CommandItem>
                    <CommandItem onSelect={() => handlePageChange("testSpec")}>
                      <FlaskConicalIcon />
                      Test spec
                    </CommandItem>
                  </>
                )}

                {page === "lab" &&
                  primaryLabs?.map((lab) => (
                    <FilterItem
                      key={lab.id}
                      value={lab.name}
                      checked={filters.labs.includes(lab.id)}
                      onToggle={() => handleLabToggle(lab.id)}
                    >
                      {lab.name}
                    </FilterItem>
                  ))}

                {page === "testType" &&
                  filteredTypeOptions.map((opt) => (
                    <FilterItem
                      key={opt.value}
                      value={opt.label}
                      checked={filters.testType.includes(opt.value)}
                      onToggle={() => handleTestTypeToggle(opt.value)}
                    >
                      <span className="capitalize">{opt.label}</span>
                    </FilterItem>
                  ))}

                {page === "testSpec" &&
                  availableTestSpecs.map((spec) => (
                    <FilterItem
                      key={spec.id}
                      value={`${spec.name} ${spec.code}`}
                      checked={filters.testSpec.includes(spec.id)}
                      onToggle={() => handleTestSpecToggle(spec.id)}
                    >
                      <span className="flex flex-1 min-w-0 items-center justify-between gap-2">
                        <span className="min-w-0 truncate">{spec.name}</span>
                        <ItemDetail>{spec.code}</ItemDetail>
                      </span>
                    </FilterItem>
                  ))}
              </CommandGroup>

              {hasFilters && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        clearFilters();
                        handleOpenChange(false);
                      }}
                    >
                      <XIcon className="size-4" />
                      <span>Clear filters</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

export function TestRunFilters({ runs }: { runs: TestRunNew[] }) {
  return <TestRunFilterBar runs={runs} />;
}

export { TestRunFilterPopover };
